import { useTranslation } from 'react-i18next';
import {
  CategoryBarChart,
  type CategoryBarDatum,
} from '@/components/common/charts/CategoryBarChart';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Card } from '@/components/ui/card';
import { useTreatmentsDeliveredQuery } from '@/hooks/useReportsQueries';
import type { ReportsDateRangeValue } from './types';
import { toDateRangeParameters } from './types';

interface TreatmentsDeliveredChartProps {
  dateRange: ReportsDateRangeValue;
}

export function TreatmentsDeliveredChart({ dateRange }: TreatmentsDeliveredChartProps) {
  const { t } = useTranslation('reports');
  const query = useTreatmentsDeliveredQuery(toDateRangeParameters(dateRange));
  const rows = query.data === undefined ? [] : query.data;
  const chartData: CategoryBarDatum[] = rows.map(function toDatum(row) {
    return { key: row.treatmentId, label: row.treatmentName, value: row.count };
  });

  return (
    <Card>
      <h2 className="text-sm font-bold text-zooindigo mb-3">
        {t('reports:charts.treatmentsDeliveredTitle')}
      </h2>
      {query.isLoading === true ? (
        <div className="py-10 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : query.isError === true ? (
        <QueryErrorState
          onRetry={function retry() {
            query.refetch();
          }}
        />
      ) : chartData.length === 0 ? (
        <p className="text-xs text-zoomuted italic py-6 text-center">
          {t('reports:charts.emptyState')}
        </p>
      ) : (
        <CategoryBarChart
          data={chartData}
          orientation="bars"
          accessibleLabel={t('reports:charts.treatmentsDeliveredTitle')}
          formatValue={formatWholeNumber}
          categoryAxisWidth={150}
        />
      )}
    </Card>
  );
}

function formatWholeNumber(value: number): string {
  return String(value);
}
